/**
 * Discriminated union for agentic chat SSE payloads (`data: { event, ... }`).
 *
 * Imported by the server as `src/shared/agenticChat/agenticStreamEvents` and by the
 * client via `~shared/agenticChat/agenticStreamEvents`. Keep this file the single
 * source of truth for stream event names and payload shapes.
 */

import type { StoryBibleGeneratedEntity } from "./storyBibleGeneratedEntity";

/** Labels streamed with write-mode POV/tense chiclets. */
export type AgenticStreamWritePovTenseChiclet = {
  label: string;
};

/** Plan object attached to `plan_proposed` (structural; client has a richer PlanData). */
export type AgenticStreamPlan = {
  workProgress?: { completed: number; total: number };
  outlineProgress?: { saved: number; total: number };
  id: string;
  description?: string;
  steps?: Array<{
    id?: string;
    title?: string;
    description?: string;
    status?: string;
  }>;
  status?: string;
  currentStepIndex?: number;
  specialConsiderations?: string;
  estimatedCreditCost?: number;
  creditCostEstimateRange?: { min?: number; max?: number; avg?: number };
  estimates?: {
    estimatedCreditCost?: number;
    creditCostEstimateRange?: { min?: number; max?: number; avg?: number };
  };
};

export type AgenticStreamAskUserQuestion = {
  question: string;
  inputType?: string;
  options?: unknown[];
};

/**
 * Canonical event names for the agentic stream contract.
 * Order is alphabetical for stable diffs / contract assertions.
 */
export const AGENTIC_STREAM_EVENT_NAMES = [
  "ask_user_question",
  "character_update",
  "chat_acknowledged",
  "chat_response_context",
  "comment_created",
  "commenting_cancelled",
  "commenting_complete",
  "commenting_started",
  "complete",
  "context_element",
  "error",
  "mentioned_entity",
  "meta_update",
  "outline_update",
  "plan_cancelled",
  "plan_completed",
  "plan_paused",
  "plan_proposed",
  "plan_step_complete",
  "plan_step_start",
  "plan_work_progress",
  "plan_outline_progress",
  "prose_chunk",
  "prose_preview",
  "reasoning_chunk",
  "recovered_message",
  "search_results",
  "story_bible_entity_generated",
  "suggested_action",
  "text",
  "text_chunk",
  "text_clear",
  "tool_complete",
  "tool_progress",
  "tool_start",
  "worldbuilding_update",
  "write_prose_context",
] as const;

export type AgenticStreamEventName = (typeof AGENTIC_STREAM_EVENT_NAMES)[number];

/** Client-only legacy cases with no current server emitter. */
export const AGENTIC_STREAM_CLIENT_LEGACY_EVENT_NAMES = [
  "context_element",
  "search_results",
] as const satisfies readonly AgenticStreamEventName[];

/** Resume fallback; handled by resumableAgenticStream, not the store switch. */
export const AGENTIC_STREAM_RESUME_EVENT_NAMES = [
  "recovered_message",
] as const satisfies readonly AgenticStreamEventName[];

export const AGENTIC_STREAM_EVENT_NAME_SET: ReadonlySet<string> = new Set(AGENTIC_STREAM_EVENT_NAMES);

export function isAgenticStreamEventName(value: unknown): value is AgenticStreamEventName {
  return typeof value === "string" && AGENTIC_STREAM_EVENT_NAME_SET.has(value);
}

type AgenticStreamEventBase<E extends AgenticStreamEventName, P extends object = {}> = {
  event: E;
} & P;

export type AgenticStreamEvent =
  | AgenticStreamEventBase<"chat_response_context", {
      preserveChatTextWithSuggestions: boolean;
    }>
  | AgenticStreamEventBase<"ask_user_question", {
      questions?: AgenticStreamAskUserQuestion[];
      /** Legacy single-question shape still accepted by the client. */
      question?: string;
      inputType?: string;
      options?: unknown[];
    }>
  | AgenticStreamEventBase<"character_update", {
      characterId?: string;
      characterName?: string;
      /** Saved card in the same shape as /api/characters/list; older clients can ignore it. */
      character?: Record<string, unknown>;
      updatedFields?: unknown;
      oldFields?: unknown;
      newFields?: unknown;
    }>
  | AgenticStreamEventBase<"chat_acknowledged", {
      chatId: string;
      model?: string;
      userMessageId?: string;
    }>
  | AgenticStreamEventBase<"comment_created", {
      commentingId: string;
      totalCreated?: number;
      feedbackTypes?: string[];
    }>
  | AgenticStreamEventBase<"commenting_cancelled", {
      commentingId: string;
    }>
  | AgenticStreamEventBase<"commenting_complete", {
      commentingId: string;
      totalCreated?: number;
    }>
  | AgenticStreamEventBase<"commenting_started", {
      commentingId: string;
      feedbackTypes?: string[];
      customFeedbackInstructions?: unknown;
    }>
  | AgenticStreamEventBase<"complete", {
    assistantMessageId?: string;
      chatId?: string;
    }>
  | AgenticStreamEventBase<"context_element", {
      elementType?: string;
      elementName?: string;
      [key: string]: unknown;
    }>
  | AgenticStreamEventBase<"error", {
      message?: string;
      error?: string;
    }>
  | AgenticStreamEventBase<"mentioned_entity", {
      entityType: string;
      entityName: string;
    }>
  | AgenticStreamEventBase<"meta_update", {
      updatedFields?: unknown;
      oldFields?: unknown;
      newFields?: unknown;
    }>
  | AgenticStreamEventBase<"outline_update", {
      chapterIds?: unknown;
      summary?: string;
      oldContent?: unknown;
      newContent?: unknown;
      rationale?: string;
    }>
  | AgenticStreamEventBase<"plan_cancelled", {
      planId: string;
      completedSteps?: number;
    }>
  | AgenticStreamEventBase<"plan_completed", {
      planId: string;
    }>
  | AgenticStreamEventBase<"plan_paused", {
      planId: string;
      currentStepIndex?: number;
    }>
  | AgenticStreamEventBase<"plan_proposed", {
      plan: AgenticStreamPlan;
    }>
  | AgenticStreamEventBase<"plan_step_complete", {
      planId: string;
      stepIndex?: number;
      stepId?: string;
      goalId?: string;
      summary?: string;
    }>
  | AgenticStreamEventBase<"plan_work_progress", { planId: string; completed: number; total: number;
      steps?: Array<{ id: string; status: "pending" | "in_progress" | "completed" | "cancelled" }> }>
  | AgenticStreamEventBase<"plan_outline_progress", { planId: string; saved: number; total: number }>
  | AgenticStreamEventBase<"plan_step_start", {
      planId: string;
      stepIndex?: number;
      stepId?: string;
      stepDescription?: string;
    }>
  | AgenticStreamEventBase<"prose_preview", { text: string; reset?: boolean; previewId?: string; replace?: boolean; explanation?: string; title?: string; groupId?: string }>
  | AgenticStreamEventBase<"prose_chunk", {
      text: string;
    }>
  | AgenticStreamEventBase<"reasoning_chunk", {
      text: string;
    }>
  | AgenticStreamEventBase<"recovered_message", {
      content: string;
      messageId: string;
    }>
  | AgenticStreamEventBase<"search_results", {
      [key: string]: unknown;
    }>
  | AgenticStreamEventBase<"story_bible_entity_generated", StoryBibleGeneratedEntity>
  | AgenticStreamEventBase<"suggested_action", {
      action: string;
      chapterId?: string;
      chapterName?: string;
      message: string;
    }>
  | AgenticStreamEventBase<"text", {
      text: string;
    }>
  | AgenticStreamEventBase<"text_chunk", {
      text: string;
    }>
  | AgenticStreamEventBase<"text_clear">
  | AgenticStreamEventBase<"tool_complete", {
      tool: string;
      callId?: string;
      outcome?: string;
      summary?: string;
      rawResult?: unknown;
      result?: unknown;
      input?: unknown;
      arguments?: unknown;
      elementType?: string;
      entityName?: string;
      model?: string;
      writePovTenseChiclets?: AgenticStreamWritePovTenseChiclet[];
      storyBibleRefresh?: Record<string, unknown>;
      /** Validated as ClientUiAction[] on the client; keep loose here for tool-result passthrough. */
      clientActions?: unknown[];
      [key: string]: unknown;
    }>
  | AgenticStreamEventBase<"tool_progress", {
      tool: string;
      callId?: string;
      message: string;
    }>
  | AgenticStreamEventBase<"tool_start", {
      tool: string;
      callId?: string;
      arguments?: unknown;
      readTargetLabel?: string;
    }>
  | AgenticStreamEventBase<"worldbuilding_update", {
      entryId?: string;
      entryName?: string;
      updatedFields?: unknown;
      oldFields?: unknown;
      newFields?: unknown;
    }>
  | AgenticStreamEventBase<"write_prose_context", {
      writePovTenseChiclets: AgenticStreamWritePovTenseChiclet[];
    }>;

/** Extract the payload member for a specific event discriminant. */
export type AgenticStreamEventOf<E extends AgenticStreamEventName> = Extract<
  AgenticStreamEvent,
  { event: E }
>;
